import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { authApi } from '../api/client';
import { getApiErrorMessage } from '../api/errors';
import { usePageTitle } from '../hooks/usePageTitle';
import { Image } from '../components/common/NextAdapters';
import styles from './LoginPage.module.css';
import { FiCheckCircle, FiXCircle } from 'react-icons/fi';

export default function ActivateAccountPage() {
  usePageTitle('Activate Account · Kast');
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('userId');
  const token = searchParams.get('token');

  const [status, setStatus] = useState('loading'); // 'loading' | 'success' | 'error'
  const [message, setMessage] = useState('');
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return; // guard against React StrictMode double-invoke
    attempted.current = true;

    if (!userId || !token) {
      setStatus('error');
      setMessage('This activation link is missing required information.');
      return;
    }

    authApi.activateAccount(userId, token)
      .then((res) => {
        setStatus('success');
        setMessage(res.data?.message || 'Your account has been activated.');
      })
      .catch((err) => {
        setStatus('error');
        setMessage(getApiErrorMessage(err, 'This activation link is invalid or has expired.'));
      });
  }, [userId, token]);

  return (
    <div className={styles.page}>
      <div className={styles.bg}>
        <Image src="/bg.png" alt="" fill priority className={styles.bgImg} />
        <div className={styles.bgOverlay} />
        <div className={styles.blob1} />
        <div className={styles.blob2} />
        <div className={styles.blob3} />
      </div>

      <motion.div
        className={styles.card}
        initial={{ opacity: 0, scale: 0.95, y: 30 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
      >
        <Link to="/" className={styles.logo} aria-label="Go to home">
          <img
            src="/cast-logo-full.png"
            alt="BoldscholarCast"
            className={styles.logoImg}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </Link>

        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
          {status === 'loading' && <span className={styles.spinner} style={{ borderTopColor: 'var(--primary)', borderColor: 'rgba(255,255,255,0.2)' }} />}
          {status === 'success' && <FiCheckCircle size={44} color="var(--primary)" />}
          {status === 'error' && <FiXCircle size={44} color="var(--destructive)" />}
        </div>

        <h1 className={styles.heading}>
          {status === 'loading' && 'Activating your account…'}
          {status === 'success' && 'Account Activated'}
          {status === 'error' && 'Activation Failed'}
        </h1>
        <p className={styles.sub}>{message}</p>

        {status !== 'loading' && (
          <Link to="/login" className={styles.submitBtn} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}>
            Go to Sign In
          </Link>
        )}
      </motion.div>
    </div>
  );
}
